/**
 * Widget configuration, read once from the embedding <script> tag:
 *
 *   <script src="esl-chat.js"
 *           data-api-url="https://api-esl-chat-v2.exaqube.com"
 *           data-market="AE"
 *           data-recaptcha-site-key="6Lc..."
 *           data-logo-url="https://.../logo.jpeg"></script>
 *
 * Every attribute is optional. Replaces the old runtime GET /config.
 */

const host = window.location.hostname;

export const IS_DEVELOPMENT = host === "localhost" || host === "127.0.0.1";
const IS_QA = host.includes("www3.");

export const CHATBOT_NAME = "ESL Bot";
export const MAX_MESSAGE_LENGTH = 1000;
export const RATE_LIMIT = { maxRequests: 18, windowMs: 60_000 };
export const MIN_PANEL_WIDTH = 280;
export const MIN_PANEL_HEIGHT = 300;

// Client-side courtesy check only — the backend's CORS_ORIGINS is the real gate.
export const AUTHORIZED_DOMAINS = [
  "www.emiratesline.com",
  "www8.emiratesline.com",
  "www.reportsnap.com",
  "demo.reportsnap.com",
  "exaqube.com", // and every subdomain, e.g. esl-chat-v2.exaqube.com
  "localhost",
  "127.0.0.1",
];

// Build-time defaults (.env, VITE_*). A data-* attribute on the script tag wins over these.
const ENV = import.meta.env;

function defaultApiUrl(): string {
  if (ENV.VITE_API_URL) return ENV.VITE_API_URL;
  if (IS_DEVELOPMENT) return "http://localhost:8000";
  if (IS_QA) return "https://esl-backend-qa.exaqube.com";
  return "https://api-esl-chat-v2.exaqube.com";
}

export interface WidgetConfig {
  apiUrl: string;
  market: string;
  /** Unset, or on localhost: no reCAPTCHA, `challenge_token` is sent as null. */
  recaptchaSiteKey: string | null;
  logoUrl: string | null;
}

export function readConfig(script: HTMLOrSVGScriptElement | null): WidgetConfig {
  const data = script instanceof HTMLScriptElement ? script.dataset : {};
  return {
    apiUrl: (data.apiUrl || defaultApiUrl()).replace(/\/+$/, ""),
    market: (data.market || ENV.VITE_MARKET || "AE").toUpperCase(),
    recaptchaSiteKey:
      IS_DEVELOPMENT || ENV.VITE_RECAPTCHA_ENABLED === "false"
        ? null
        : data.recaptchaSiteKey || ENV.VITE_RECAPTCHA_SITE_KEY || null,
    logoUrl: data.logoUrl || ENV.VITE_LOGO_URL || null,
  };
}

export function isAuthorizedDomain(): boolean {
  return AUTHORIZED_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`));
}
